import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import NFLStatEngine from "./NFLStatEngine";
import NFLToolLayout, { SeasonSelect } from "../__shared/NFLToolLayout";
import FilterBar from "../__shared/FilterBar";
import PositionFilter from "../__shared/PositionFilter";
import WeekFilter from "../__shared/WeekFilter";
import TeamFilter from "../__shared/TeamFilter";
import ToolStatePanel, { TableSkeleton } from "../__shared/ToolStatePanel";
import ToolErrorBoundary from "../__shared/ToolErrorBoundary";
import useNflReference from "../__shared/useNflReference";
import useUrlFilters from "../__shared/useUrlFilters";
import useDebouncedValue from "../__shared/useDebouncedValue";
import {
  playedWeeks,
  resolveTeamIds,
  sameWeeks,
  seasonFor,
  weeksLabel,
} from "../__shared/nflToolFilters";
import { isToolSub } from "../../../../../AuthUtils";

const POSITIONS = ["QB", "RB", "WR", "TE"];
const FILTER_OPTIONS = { positions: POSITIONS };
const TITLE = "Stat Engine";
const SUBTITLE = "Usage and market share for every player, by week.";
const SKELETON_COLUMNS = ["Player", "G", "Att", "Yds", "TD", "Tgt", "Rec", "Total"];

export default function NFLStatEnginePage() {
  const [referenceFetchKey, setReferenceFetchKey] = useState(0);

  return (
    <ToolErrorBoundary
      what="Stats"
      onRetry={() => setReferenceFetchKey((key) => key + 1)}
      wrapError={(panel) => (
        <NFLToolLayout tool="stat-engine" title={TITLE} subtitle={SUBTITLE}>
          {panel}
        </NFLToolLayout>
      )}
    >
      <React.Suspense
        fallback={
          <NFLToolLayout tool="stat-engine" title={TITLE} subtitle={SUBTITLE}>
            <TableSkeleton columns={SKELETON_COLUMNS} />
          </NFLToolLayout>
        }
      >
        <StatEngineTool referenceFetchKey={referenceFetchKey} />
      </React.Suspense>
    </ToolErrorBoundary>
  );
}

function StatEngineTool({ referenceFetchKey }) {
  const reference = useNflReference({ fetchKey: referenceFetchKey });
  const filters = useUrlFilters(FILTER_OPTIONS);
  const [fetchKey, setFetchKey] = useState(0);
  const [rowCount, setRowCount] = useState(null);

  const season = seasonFor(filters.season, reference);
  const played = useMemo(() => playedWeeks(season), [season]);
  const selectedWeeks = filters.weeks || played;

  const teamByAbbrev = useMemo(() => {
    const map = {};
    reference.teams.forEach((team) => {
      map[team.abbreviation] = team;
    });
    return map;
  }, [reference.teams]);

  const knownTeams = useMemo(
    () => filters.teams.filter((abbrev) => teamByAbbrev[abbrev]),
    [filters.teams, teamByAbbrev]
  );

  const teamIds = useMemo(
    () => resolveTeamIds(knownTeams, reference.teams),
    [knownTeams, reference.teams]
  );

  const weeksKey = selectedWeeks.join(",");
  const combinedKey = `${season ? season.id : ""}|${weeksKey}`;
  const debouncedKey = useDebouncedValue(combinedKey, 400);
  const variables = useMemo(() => {
    const pipeIndex = debouncedKey.indexOf("|");
    const seasonID = debouncedKey.slice(0, pipeIndex);
    const weeksPart = debouncedKey.slice(pipeIndex + 1);
    return {
      seasonID: seasonID || null,
      weeks: weeksPart ? weeksPart.split(",").map(Number) : [],
    };
  }, [debouncedKey]);
  const deferredVariables = useDeferredValue(variables);
  const isUpdating = combinedKey !== debouncedKey || variables !== deferredVariables;

  const retry = useCallback(() => {
    setRowCount(null);
    setFetchKey((key) => key + 1);
  }, []);

  if (!isToolSub(reference.groups)) {
    return <Redirect to="/memberships" />;
  }

  const chips = [
    ...filters.positions.map((pos) => ({
      key: `pos-${pos}`,
      label: pos,
      onRemove: () => filters.togglePosition(pos),
    })),
    ...(filters.weeks
      ? [{ key: "weeks", label: weeksLabel(filters.weeks), onRemove: () => filters.setWeeks(null) }]
      : []),
    ...knownTeams.map((abbrev) => {
      const team = teamByAbbrev[abbrev];
      return {
        key: `team-${abbrev}`,
        label: team ? team.name : abbrev,
        color: team ? team.primaryColorHex : undefined,
        onRemove: () => filters.toggleTeam(abbrev),
      };
    }),
  ];

  const clearAll = chips.length > 0 ? () => filters.clear() : undefined;
  const lastChip = chips[chips.length - 1];

  const emptyState =
    chips.length > 0 ? (
      <ToolStatePanel
        title="No players match these filters"
        body={chips.map((chip) => chip.label).join(" · ")}
        actions={
          <>
            <button type="button" className="nfl-btn" onClick={lastChip.onRemove}>
              Remove {lastChip.label}
            </button>
            <button type="button" className="nfl-btn nfl-btn-ghost" onClick={clearAll}>
              Clear all filters
            </button>
          </>
        }
      />
    ) : (
      <ToolStatePanel
        title={`No stats for ${season ? season.year : "this season"} yet`}
        body="Stats show up here after games are played."
      />
    );

  return (
    <NFLToolLayout
      tool="stat-engine"
      title={TITLE}
      subtitle={SUBTITLE}
      actions={
        <SeasonSelect
          seasons={reference.seasons}
          value={season ? season.year : ""}
          onChange={(year) =>
            filters.setSeason(
              reference.currentSeason && year === reference.currentSeason.year ? null : year
            )
          }
        />
      }
      filters={
        <FilterBar
          primary={
            <>
              <PositionFilter
                options={POSITIONS}
                selected={filters.positions}
                onToggle={filters.togglePosition}
              />
              <WeekFilter
                weeks={season ? season.weeks : []}
                playedThrough={season ? season.playedThrough : 0}
                selected={selectedWeeks}
                onChange={(next) =>
                  filters.setWeeks(next.length === 0 || sameWeeks(next, played) ? null : next)
                }
              />
            </>
          }
          secondary={
            <TeamFilter
              teams={reference.teams}
              selected={knownTeams}
              onToggle={filters.toggleTeam}
            />
          }
          chips={chips}
          onClearAll={clearAll}
          resultCount={rowCount}
          resultNoun="players"
          isUpdating={isUpdating}
        />
      }
    >
      <ToolErrorBoundary what="Stats" onRetry={retry}>
        <React.Suspense fallback={<TableSkeleton columns={SKELETON_COLUMNS} />}>
          <div className={isUpdating ? "nfl-is-updating" : undefined}>
            <NFLStatEngine
              variables={deferredVariables}
              fetchKey={fetchKey}
              positions={filters.positions}
              teamIds={teamIds}
              teams={reference.teams}
              onRowCount={setRowCount}
              emptyState={emptyState}
            />
          </div>
        </React.Suspense>
      </ToolErrorBoundary>
    </NFLToolLayout>
  );
}
